import { ContextMenuOption } from '../../../models/context-menu-option';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint } from '../../../models/enums';
import { LoadFileProjectTemplate, LoadFileProjectTemplateViewModel } from '../../../models/load-file-project-template';
import { ExportMetadataProfile, ExportMetadataProfileViewModel } from '../../../models/export-metadata-profile';
import { NavigationInfo } from '../../../models/navigation-info';
import { TemplateType } from '../../../models/template';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { ObjectActionListType, PermissionSet, ActionType, Permissions } from '../../../models/permission';

const saveToLoadFromContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Save to Template ...', iconClass: 'fa-save', iconColor: 'color-blue-01',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })],
        orgPermissionAction: ActionType.CREATE
    }),
    new ContextMenuOption({
        name: 'Load from Template ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => item.data.templateType !== TemplateType.CUSTOM_FIELDS,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE], isMandatory: true })],
        atLeastOne: true,
        orgPermissionAction: ActionType.VIEW
    }),
    new ContextMenuOption({
        divider: true,
        visible: (item: GenericTreeNode) => item.data.templateType === TemplateType.DOCUMENT_METADATA || item.data.templateType === TemplateType.EXPORT_FIELDS
    }),
    new ContextMenuOption({
        name: 'Download as XML ...', iconClass: 'fa-download', iconColor: 'color-blue-01',
        visible: (item: GenericTreeNode) => item.data.templateType === TemplateType.DOCUMENT_METADATA || item.data.templateType === TemplateType.EXPORT_FIELDS,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE], isMandatory: true })],
        orgPermissionAction: ActionType.VIEW
    }),
    new ContextMenuOption({
        name: 'Load from XML ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => item.data.templateType === TemplateType.DOCUMENT_METADATA || item.data.templateType === TemplateType.EXPORT_FIELDS,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE], isMandatory: true })],
        atLeastOne: true,
        orgPermissionAction: ActionType.VIEW,
        tooltip: 'A new template will be created from the XML file'
    }),
];
const headerContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Download All as XMLs ...', iconClass: 'fa-download', iconColor: 'color-blue-01',
        visible: (node: GenericTreeNode) => (node as GenericTreeNode).name === 'Templates' && node.children[0]?.bundle === TemplateType.EXPORT_FIELDS || (node as GenericTreeNode).name === 'Export Fields' || (node as GenericTreeNode).name === 'Metadata View Fields',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Load from XMLs ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (node: GenericTreeNode) => (node as GenericTreeNode).name === 'Templates' && node.children[0]?.bundle === TemplateType.EXPORT_FIELDS || (node as GenericTreeNode).name === 'Export Fields' || (node as GenericTreeNode).name === 'Metadata View Fields',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })],
        tooltip: 'A new template will be created from each XML file'
    })
];
const templatesContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Save to Template ...', iconClass: 'fa-save', iconColor: 'color-blue-01',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })],
    }),
    new ContextMenuOption({
        name: 'Load from Template ...',
        iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        divider: true,
        visible: (item: GenericTreeNode) => !!(item.data as ExportMetadataProfile)?.profileType,
    }),
    new ContextMenuOption({
        name: 'Download as XML ...', iconClass: 'fa-download', iconColor: 'color-blue-01',
        visible: (item: GenericTreeNode) => !!(item.data as ExportMetadataProfile)?.profileType,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Load from XML ...', iconClass: 'fa-folder-open', iconColor: 'color-yellow-07',
        visible: (item: GenericTreeNode) => !!(item.data as ExportMetadataProfile)?.profileType,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })],
        tooltip: 'A new template will be created from the XML file'
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Set as Default', iconClass: 'fa-check', iconColor: 'color-green-04',
        enabled: (item: GenericTreeNode) => !(item.data as LoadFileProjectTemplate).defaultProfile,
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', enabled: (item: GenericTreeNode) => !(item.data as LoadFileProjectTemplate).defaultProfile,
        permissions: [new PermissionSet({ permissionActions: [ActionType.DELETE] })]
    })
];
const templateHeaderButtons: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'New Template ...', iconClass: 'fa-plus', iconColor: 'color-green-04',
        permissions: [new PermissionSet({ permissionActions: [ActionType.CREATE] })]
    })
];

export const ProjectMenu: Array<GenericTreeNode> = [
    new GenericTreeNode({
        name: 'General',
        automationId: 'proj-settings-tree-general',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,

        children: [
            new GenericTreeNode({
                name: 'Preferences',
                automationId: 'proj-settings-tree-general-preferences',
                icon: 'fa-gear',
                iconColor: 'color-blue-01',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-preferences', default: true }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PROJECT_PREFERENCES })],
            }),
            new GenericTreeNode({
                name: 'Email Delivery',
                automationId: 'proj-settings-tree-general-email-delivery',
                icon: 'fa-envelope',
                iconColor: 'color-blue-01',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-email-delivery' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.EMAIL_DELIVERY })],
            }),
            new GenericTreeNode({
                name: 'Project Members',
                automationId: 'proj-settings-tree-general-project-members',
                icon: 'fa-users',
                iconColor: 'color-blue-01',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-project-members' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PROJECT_MEMBERS })],
            })
        ]
    }),
    new GenericTreeNode({
        name: 'Reef Express',
        automationId: 'proj-settings-tree-tlsexpress',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,
        permissions: [new PermissionSet({ permissionType: ObjectActionListType.TLS_PROJECT_CONFIG })],

        children: [
            new GenericTreeNode({
                name: 'Project Configuration',
                automationId: 'proj-settings-tree-tlsexpress-project-configuration',
                icon: 'fa-sliders',
                iconColor: 'color-blue-01',
                childPadding: 5,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-tls-project-configuration' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.TLS_PROJECT_CONFIG })]
            })
        ]
    }),
    new GenericTreeNode({
        name: 'eDiscovery',
        automationId: 'proj-settings-tree-ediscovery',
        isExpandable: true,
        isExpanded: true,
        isExpandedOnSelect: true,
        isSelectable: false,
        hideExpander: true,

        children: [
            new GenericTreeNode({
                name: 'Alias Lists',
                automationId: 'proj-settings-tree-ediscovery-alias-lists',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-alias-lists' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_ALIAS_LISTS })],
                data: { templateType: TemplateType.ALIAS_LISTS }
            }),
            new GenericTreeNode({
                name: 'Analytic Settings',
                automationId: 'proj-settings-tree-ediscovery-analytic-settings',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-analytic-settings' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_ANALYTIC_SETTINGS })],
                data: { templateType: TemplateType.ANALYTICAL_SETTINGS }
            }),
            new GenericTreeNode({
                name: 'Billing Reports',
                automationId: 'proj-settings-tree-ediscovery-billing-reports',
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-billing-report' }),
                childPadding: 30,
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_BILLING_REPORTS })],
                data: { templateType: TemplateType.BILLING_REPORT_SETTINGS }
            }),
            new GenericTreeNode({
                name: 'Custom Fields',
                automationId: 'proj-settings-tree-ediscovery-custom-fields',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-custom-fields' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_CUSTOM_FIELDS })],
                data: { templateType: TemplateType.CUSTOM_FIELDS }
            }),
            new GenericTreeNode({
                name: 'Domain Lists',
                automationId: 'proj-settings-tree-ediscovery-domain-lists',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-domain-lists' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_DOMAIN_LISTS })],
                data: { templateType: TemplateType.DOMAIN_LISTS }
            }),
            new GenericTreeNode({
                name: 'Excluded Content',
                automationId: 'proj-settings-tree-ediscovery-excluded-content',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-excluded-content' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_EXCLUDED_CONTENT })],
                data: { templateType: TemplateType.EMAIL_SIGNATURE }
            }),
            new GenericTreeNode({
                name: 'Export Fields',
                automationId: 'proj-settings-tree-ediscovery-export-fields',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                icon: 'fa-sticky-note-o',
                iconColor: 'color-blue-01',
                iconSecond: 'fa-sticky-note',
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_EXPORT_FIELDS })],
                contextMenuOptions: headerContextMenuOptions,
                showContextMenuButton: true,
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.projectManager_listExportMetadataProfile,
                    responseName: 'profiles'
                }),
                createChild: (data: any): GenericTreeNode => {
                    const item = new ExportMetadataProfile(data);
                    const viewModel = new ExportMetadataProfileViewModel(item);
                    return new GenericTreeNode({
                        name: viewModel.model.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-export-fields' }),
                        customText: viewModel.model.defaultProfile ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-blue-01',
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.PRJ_EXPORT_FIELDS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        bundle: viewModel.templateType,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_EXPORT_FIELDS })],
                    });
                },
            }),
            new GenericTreeNode({
                name: 'Export Settings',
                automationId: 'proj-settings-tree-ediscovery-export-settings',
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-export-settings' }),
                childPadding: 30,
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_EXPORT_SETTINGS })],
                data: { templateType: TemplateType.EXPORT_SETTINGS }
            }),
            new GenericTreeNode({
                name: 'Index Settings',
                automationId: 'proj-settings-tree-ediscovery-index-settings',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-index-settings' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_INDEX_SETTINGS })],
                data: { templateType: TemplateType.INDEX_SETTINGS }
            }),
            new GenericTreeNode({
                name: 'Load File Import',
                automationId: 'proj-settings-tree-ediscovery-load-file-import',
                icon: 'fa-sticky-note-o',
                iconSecond: 'fa-sticky-note',
                iconColor: 'color-blue-01',
                childPadding: 5,
                headerButtons: templateHeaderButtons,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_LOADFILE_SETTINGS })],
                requestInfo: new DrRequestInfo({
                    endpoint: Endpoint.projectManager_listLoadFileMetadataProfiles,
                    responseName: 'profiles'
                }),
                createChild: (data: any): GenericTreeNode => {
                    const item = new LoadFileProjectTemplate(data);
                    const viewModel = new LoadFileProjectTemplateViewModel(item);
                    return new GenericTreeNode({
                        name: viewModel.model.name,
                        automationId: 'load-file-template-' + viewModel.model.name,
                        navigationInfo: new NavigationInfo({ name: '', path: 'settings-load-file-import' }),
                        customText: viewModel.model.defaultProfile ? 'Default' : '',
                        icon: 'fa-sticky-note',
                        iconColor: 'color-blue-01',
                        showContextMenuButton: Permissions.checkUserPermission(ObjectActionListType.PRJ_LOADFILE_SETTINGS, ActionType.CREATE),
                        contextMenuOptions: templatesContextMenuOptions,
                        data: item,
                        bundle: viewModel.templateType,
                        permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_LOADFILE_SETTINGS })],
                    });
                }
            }),
            new GenericTreeNode({
                name: 'Metadata Search Fields',
                automationId: 'proj-settings-tree-ediscovery-metadata-search-fields',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-metadata-fields' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_METADATA_SEARCH_FIELDS })],
                data: { templateType: TemplateType.SEARCH_FIELDS }
            }),
            new GenericTreeNode({
                name: 'Metadata View Fields',
                automationId: 'proj-settings-tree-ediscovery-metadata-view-fields',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-metadata-fields' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_METADATA_VIEW_FIELDS })],
                data: { templateType: TemplateType.DOCUMENT_METADATA }
            }),
            new GenericTreeNode({
                name: 'Password Cracking',
                automationId: 'proj-settings-tree-ediscovery-password-cracking',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-password-cracking' }),
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_PASSWORD_CRACKING })],
                data: { templateType: TemplateType.META_TEMPLATE_PROFILE }
            }),
            new GenericTreeNode({
                name: 'Patterns',
                automationId: 'proj-settings-tree-ediscovery-patterns',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-patterns' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_REGULAR_EXPRESSIONS })],
                data: { templateType: TemplateType.USER_EXP }
            }),
            new GenericTreeNode({
                name: 'Reports',
                automationId: 'proj-settings-tree-ediscovery-reports',
                childPadding: 30,
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                data: { templateType: TemplateType.REPORT_SETTINGS },
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_REPORT_SETTINGS })],
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-report-settings' })
            }),
            new GenericTreeNode({
                name: 'Search Settings',
                automationId: 'proj-settings-tree-ediscovery-search-options',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-search-settings' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_SEARCH_SETTINGS })],
                data: { templateType: TemplateType.SEARCH_SETTINGS }
            }),
            new GenericTreeNode({
                name: 'Survivorship Model',
                automationId: 'proj-settings-tree-ediscovery-survivorship-model',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-survivorship' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_SURVIVORSHIP_SETTINGS })],
                data: { templateType: TemplateType.DUPE_SURVIVORSHIP }
            }),
            new GenericTreeNode({
                name: 'Tags',
                automationId: 'proj-settings-tree-ediscovery-tags',
                childPadding: 30,
                navigationInfo: new NavigationInfo({ name: '', path: 'settings-tags-settings' }),
                showContextMenuButton: true,
                contextMenuOptions: saveToLoadFromContextMenuOptions,
                permissions: [new PermissionSet({ permissionType: ObjectActionListType.PRJ_TAGS })],
                data: { templateType: TemplateType.TAG }
            })
        ]
    })
];
